import { lazy, useCallback } from 'react';
import { Button as MuiButton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useInstanceSelector, useSelector } from '@client/hooks/redux.hooks';
import { getApplyTzForShow, getCurrentShow } from '@client/selectors/entities/shows';
import { inProgress } from '@client/selectors/helpers/loaders';
import { Modal } from '@client/components/globals/Modal';
import { LoadingButton } from '@client/components/common/LoadingButton';
import { Loader } from '@client/components/common/Loader';
import { resetCurrentSKUAmount } from '@client/slices/features/stripe.reducer';
import type { Badge } from '@client/hooks/useTicketingCheck';

const useStyles = makeStyles()((theme) => ({
  modalDialogTitleProps: {
    borderBottomColor: theme.palette.divider,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    marginBottom: 0,
    paddingLeft: theme.spacing(3),
  },
}));

interface TicketButtonProps {
  readonly showId: string;
  readonly badge: Badge;
  readonly canPurchaseTickets: boolean;
}

const TicketModalLoader = lazy(() => import(/* webpackPrefetch: true */ './TicketModal'));

export const TicketButton: React.FC<TicketButtonProps> = ({ showId, badge, canPurchaseTickets }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const show = useSelector(getCurrentShow);
  const applyTz = useSelector(getApplyTzForShow);

  const scope = `fetch-${showId}-SKU`;
  const stripeLoaderInProgress = useInstanceSelector(inProgress, scope);

  const handleTicketPurchaseButtonClick = useCallback(() => {
    dispatch(resetCurrentSKUAmount());

    Modal.show({
      body: <Loader load={TicketModalLoader} />,
      buttons: [],
      maxWidth: 'narrow',
      fullWidth: true,
      DialogTitleProps: {
        className: classes.modalDialogTitleProps,
      },
      title: (
        <Typography variant="body2">
          {show?.title}
        </Typography>
      ),
      subtitle: (
        <Typography
          color="textSecondary"
          component="span"
        >
          {show.city}
          ,

          {' '}

          {applyTz(show?.startTime).toFormat('MMMM d, h:mm a')}
        </Typography>
      ),
    });
  }, [show?.startTime, show?.title, applyTz, show?.city, dispatch, classes.modalDialogTitleProps]);

  if (stripeLoaderInProgress !== false) {
    return (
      <LoadingButton
        variant="contained"
        color="success"
        size="large"
        aria-label="buy tickets"
        aria-busy="true"
        fullWidth
      />
    );
  }

  if (canPurchaseTickets) {
    return (
      <MuiButton
        aria-label="buy tickets"
        variant="contained"
        size="large"
        color="success"
        onClick={handleTicketPurchaseButtonClick}
        fullWidth
      >
        Tickets
      </MuiButton>
    );
  }

  return (
    <Tooltip title={badge.long}>
      <span>
        <MuiButton
          variant="contained"
          size="large"
          color="success"
          fullWidth
          disabled
        >
          {badge.short}
        </MuiButton>
      </span>
    </Tooltip>
  );
};
